/** Badge tiles, the profile badge grid and the «new badge» overlay. */
import { useEffect, useState } from 'react';
import { achievementInfo, type AchievementIcon, type BadgeView } from '../core/achievements.ts';
import { subscribeUnlocks } from '../data/achievements.ts';
import { Flame, Icon, type IconName } from './Icon.tsx';
import { RoboCat } from './RoboCat.tsx';
import { playFeedback } from './feedback.ts';
import './badges.css';

const ICON: Record<Exclude<AchievementIcon, 'flame'>, IconName> = {
  cards: 'cards',
  book: 'book',
  books: 'books',
  star: 'star',
  moon: 'moon',
  sun: 'sun',
  trophy: 'league',
  bolt: 'bolt',
};

export function BadgeIcon({ icon, size = 26 }: { icon: AchievementIcon; size?: number }) {
  return icon === 'flame' ? <Flame size={size} /> : <Icon name={ICON[icon]} size={size} />;
}

export function BadgeMedal({ icon, earned, size = 56 }: { icon: AchievementIcon; earned: boolean; size?: number }) {
  return (
    <span
      className={`badge-medal badge-medal--${icon}${earned ? '' : ' badge-medal--locked'}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {earned ? <BadgeIcon icon={icon} size={Math.round(size * 0.46)} /> : <Icon name="lock" size={Math.round(size * 0.4)} />}
    </span>
  );
}

export function BadgeGrid({ badges }: { badges: readonly BadgeView[] }) {
  return (
    <ul className="badge-grid">
      {badges.map((b) => (
        <li key={b.code} className={`badge-tile${b.earned ? '' : ' badge-tile--locked'}`}>
          <BadgeMedal icon={b.icon} earned={b.earned} />
          <span className="badge-tile__title">
            {b.title}
            {b.count > 1 && <span className="badge-tile__count"> ×{b.count}</span>}
          </span>
          {!b.earned && <span className="badge-tile__how">{b.howTo}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Shows each newly earned badge once, with the happy robo-cat. Mounted once in the app. */
export function AchievementUnlock() {
  const [queue, setQueue] = useState<string[]>([]);

  useEffect(
    () =>
      subscribeUnlocks((codes) => {
        playFeedback('complete');
        setQueue((q) => [...q, ...codes]);
      }),
    [],
  );

  const code = queue[0];
  if (!code) return null;
  const info = achievementInfo(code);
  const dismiss = () => setQueue((q) => q.slice(1));

  return (
    <div className="unlock-backdrop" onClick={(e) => e.target === e.currentTarget && dismiss()}>
      <div className="unlock" role="dialog" aria-modal="true" aria-labelledby="unlock-title">
        <div className="unlock__stage">
          <span className="unlock__rays" aria-hidden="true" />
          <BadgeMedal icon={info.icon} earned size={96} />
          <span className="unlock__cat">
            <RoboCat size={52} state="happy" />
          </span>
        </div>
        <p className="unlock__caps">Новое достижение!</p>
        <h2 id="unlock-title" className="unlock__title">
          {info.title}
        </h2>
        <p className="unlock__how">{info.howTo}</p>
        <button type="button" className="btn btn--primary btn--block" onClick={dismiss} autoFocus>
          Ура!
        </button>
      </div>
    </div>
  );
}
